import {connect} from 'react-redux';

import {createSelector, makeMapStateToProps} from '../../../../../srcCommon/helpers/reselectHelpers';
import {buildEditViewSettingsPath} from '../../../../helpers/modelHelpers';
import {VisualCustomView} from '../../../../helpers/models/customViewHelpers';
import {TeamModel} from '../../../../models/api/teamModel';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
  WorkspaceDisplayActionsEnum,
  WorkspaceDisplaySectionTrackingTypesEnum,
} from '../../../../services/tracking';
import {AppState} from '../../../../store/appState';
import {getCustomViewBackfillJobs} from '../../../../store/data/jobs/jobsSelectors';
import {
  makeGetCounterBadgeValue,
  makeGetCounterIsUnread,
} from '../../../../store/data/preferences/countersSelectors';
import {toggleHideTagAction} from '../../../../store/data/preferences/preferencesActions';
import {WorkspaceDropReorderTeamViewHandler} from '../../../../store/data/workspace/reorder/types';
import {getShouldRecommendDeclutter} from '../../../../store/data/workspace/visualWorkspace/visualWorkspaceSelectors';
import {hideWorkspaceTagAsyncAction} from '../../../../store/data/workspace/workspaceActions';
import {startedEditingWorkspacesAction} from '../../../../store/panels/inboxes/inboxesPanelActions';
import {navigateToLinkAction} from '../../../../store/stateless/navigationActions';
import {canDropWorkspaceItemAtTarget} from '../dnd/workspaceDragDropHelpers';
import {WorkspaceDragSourceTypesEnum, WorkspaceItemDropHandler} from '../dnd/workspaceDragDropTypes';
import {WorkspaceLoadingTypesEnum} from '../line/workspaceLineMore';
import {
  makeGetIsSelected,
  makeGetIsUnreadBadge,
  WorkspaceItemContainerProps,
} from '../workspaceItemContainerHelpers';
import {TeamWorkspaceCustomViewItem} from './teamWorkspaceCustomViewItem';

export interface TeamWorkspaceCustomViewItemContainerProps extends WorkspaceItemContainerProps {
  customView: VisualCustomView;
  hasEditPermission: boolean;
  counterKey?: string;
  team: TeamModel;
  isLastInList: boolean;
  onDropReorderTeamView: WorkspaceDropReorderTeamViewHandler;
}

/** Extract the tag id of the "customView" property from the props. */
function getCustomViewTagId(_state: AppState, props: TeamWorkspaceCustomViewItemContainerProps) {
  return props.customView.id;
}

const getTagIdsFromBackfillJobs = createSelector([getCustomViewBackfillJobs], (runningBackfillJobs) =>
  runningBackfillJobs.map((job) => job.tagId),
);

function makeGetLoadingType() {
  return createSelector([getCustomViewTagId, getTagIdsFromBackfillJobs], (tagId, runningJobsTagIds) =>
    runningJobsTagIds.includes(tagId) ? WorkspaceLoadingTypesEnum.BACKFILL_CUSTOM_VIEW : undefined,
  );
}

const mapStateToProps = makeMapStateToProps<TeamWorkspaceCustomViewItemContainerProps>()(() => {
  const badgeValue = makeGetCounterBadgeValue();
  return {
    badgeValue,
    isSelected: makeGetIsSelected(),
    isUnreadBadge: makeGetIsUnreadBadge(),
    isUnread: makeGetCounterIsUnread(badgeValue),
    loadingType: makeGetLoadingType(),
    hasRarelyViewedItems: getShouldRecommendDeclutter,
  };
});

export const TeamWorkspaceCustomViewItemContainer = connect(
  mapStateToProps,
  {
    toggleHideTag: toggleHideTagAction,
    navigateToLink: navigateToLinkAction,
    hideWorkspaceTag: hideWorkspaceTagAsyncAction,
    startedEditingWorkspaces: startedEditingWorkspacesAction,
  },
  (stateProps, dispatchProps, ownProps) => {
    const {customView, onDropReorderTeamView} = ownProps;

    const onHideClick = () => {
      dispatchProps.hideWorkspaceTag(customView.tag.id, customView.tag.isView, customView.tag.namespace);
      dispatchProps.toggleHideTag(customView.tag.id, [customView.tag], {value: false});
    };

    const onEditClick = () => {
      const settingsPath = buildEditViewSettingsPath(
        ownProps.customView.namespace,
        ownProps.customView.tag.id,
      );
      dispatchProps.navigateToLink(settingsPath);

      // Tracking.
      trackInterfaceEvent(InterfaceEventsEnum.EDIT_VIEW);
    };

    const onEditWorkspaces = () => {
      dispatchProps.startedEditingWorkspaces(ownProps.panelId, stateProps.hasRarelyViewedItems);

      trackInterfaceEvent(InterfaceEventsEnum.P1_WORKSPACE_CLICK, {
        action: WorkspaceDisplayActionsEnum.VIEW_SIDEBAR,
        section: WorkspaceDisplaySectionTrackingTypesEnum.CUSTOM_VIEW,
      });
    };

    const onWorkspaceItemDrop: WorkspaceItemDropHandler = (droppedItem, linePosition, target) => {
      if (!target || !canDropWorkspaceItemAtTarget(droppedItem, target)) {
        return;
      }

      if (
        droppedItem.type !== WorkspaceDragSourceTypesEnum.TEAM_VIEW ||
        target.type !== WorkspaceDragSourceTypesEnum.TEAM_VIEW
      ) {
        return;
      }

      onDropReorderTeamView({
        teamId: ownProps.team.id,
        droppedItemId: droppedItem.id,
        targetItemId: target.id,
        linePosition,
      });
    };

    return {
      ...stateProps,
      ...ownProps,
      onEditClick,
      onHideClick,
      onWorkspaceItemDrop,
      onEditWorkspaces,
    };
  },
)(TeamWorkspaceCustomViewItem);
